import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { autoUpdate, flip, offset, safePolygon, shift, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole } from '@floating-ui/react';
import { useMountEffect } from '@react-hookz/web';
import { useBreakpointUp } from '@travel-frontend/core';
import { IconAccountCircle } from '@travel-frontend/icons';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { BurgerMenu } from '../../components/AccountControl/components/BurgerMenu/index.js';
import { HeaderControl } from '../../components/AccountControl/components/HeaderControl.js';
import { AuthenticationWidget } from '../../components/Authentication/index.js';
import { useAuthentication } from '../../hooks/index.js';
import { Avatar, LabelSmall, Skeleton } from '../../primitives/index.js';
import { Portal } from '../../primitives/internal/Portal/index.js';
import { HEADER_QA_ID, qaId } from '../../utils/qaId.js';
// @TODO Move to HeaderNew folder after all funnels migrate to new header
export const AccountControl = ({
  onLogout,
  enableCaptionForAnonymous
}) => {
  const [open, setOpen] = useState(false);
  const isLargeScreen = useBreakpointUp('xl');
  const {
    t
  } = useTranslation('headerNew');
  const {
    isLoggedIn,
    userInfo,
    getUserStatus
  } = useAuthentication();
  const {
    firstName,
    lastName,
    initials
  } = userInfo || {};
  useMountEffect(() => {
    getUserStatus();
  });
  const {
    refs,
    context,
    floatingStyles
  } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(16), flip(), shift({
      padding: 10
    })],
    whileElementsMounted: autoUpdate
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([useHover(context, {
    mouseOnly: true,
    handleClose: safePolygon()
  }), useFocus(context), useDismiss(context), useRole(context, {
    role: 'menu'
  }), useClick(context)]);
  return _jsxs(_Fragment, {
    children: [!isLoggedIn && _jsx(AuthenticationWidget, {
      onSuccessSignIn: getUserStatus
    }), _jsx(HeaderControl, {
      as: 'div',
      tabIndex: 0,
      "data-testid": qaId(HEADER_QA_ID, 'headercontrol', 'login'),
      "data-clarity-mask": "true",
      ref: refs.setReference,
      ...getReferenceProps(),
      children: isLoggedIn ? _jsxs(_Fragment, {
        children: [_jsx(Avatar, {
          initials: initials
        }), isLargeScreen && (firstName && lastName ? _jsx(LabelSmall, {
          truncate: true,
          color: "contentInversePrimary",
          "data-testid": qaId(HEADER_QA_ID, 'headercontrol', 'fullname'),
          children: `${firstName} ${lastName}`
        }) : _jsx(Skeleton, {
          variant: "text",
          width: 70,
          height: 20
        }))]
      }) : _jsxs(_Fragment, {
        children: [_jsx(IconAccountCircle, {}), enableCaptionForAnonymous && t('headerNew:login.myAccount')]
      })
    }), open && _jsx(Portal, {
      children: _jsx(BurgerMenu, {
        ref: refs.setFloating,
        style: floatingStyles,
        "data-testid": qaId(HEADER_QA_ID, 'headercontrol', 'burgermenu'),
        onClickItem: () => {
          setOpen(false);
        },
        onLogout: onLogout,
        ...getFloatingProps()
      })
    })]
  });
};